import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { askDrc, latestChatHelp, loadDrcChat, plainAnswer, ADDED_NOTE } from '@/lib/drc';
import { useAuth } from '@/lib/auth';
import { useChats } from '@/data/store';
import { SendIcon } from './Icons';
import { DrcAnswer } from './DrcAnswer';
import { STAFF_ASSISTANT_NAME } from '@/lib/assistant';

// The live Dr Cleanstein thread, used in a chat's Dr C sheet (about that customer)
// and on the Dr C tab (anything in Sentinel). Answers come from the real engine
// under the operator's own access and are for the operator only. "Use in reply"
// only fills the message box; nothing is ever sent from here.

interface Entry {
  question: string;
  answer: string;
  step: string | null;
  error: string | null;
  done: boolean;
}

interface Thread {
  drcConversationId: string | null;
  entries: Entry[];
}

// One thread per chat (and one for the tab) while the app is open, so closing and
// reopening the sheet keeps the conversation.
const threads = new Map<string, Thread>();

interface Props {
  /** The open WhatsApp chat, or null on the Dr C tab. */
  chatId: string | null;
  chips: string[];
  placeholder: string;
  onUseReply?: (line: string) => void;
  /** On the Dr C tab: the intro shown above the thread, which then fills the page with the ask bar below. */
  pageIntro?: ReactNode;
}

export function DrcLive({ chatId, chips, placeholder, onUseReply, pageIntro }: Props) {
  const key = chatId ?? '__tab__';
  const [thread, setThread] = useState<Thread>(() => threads.get(key) ?? { drcConversationId: null, entries: [] });
  const [typed, setTyped] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const busy = thread.entries.some((e) => !e.done);

  useEffect(() => {
    threads.set(key, thread);
  }, [key, thread]);

  // Inside a customer chat, the help asked there before comes back (Gary, 15 Oct), also
  // after the app was closed. Saved with the chat, never in Ask history.
  const { session } = useAuth();
  const { live } = useChats();
  const userId = session?.user.id;
  useEffect(() => {
    if (!live || !chatId || !userId || threads.get(key)?.entries.length) return;
    let gone = false;
    void (async () => {
      const id = await latestChatHelp(userId, chatId);
      if (!id || gone) return;
      const turns = await loadDrcChat(id).catch(() => []);
      if (gone || !turns.length) return;
      setThread((t) => (t.entries.length ? t : {
        drcConversationId: id,
        entries: turns.map((x) => ({ question: x.question, answer: x.answer, step: null, error: null, done: true })),
      }));
    })();
    return () => { gone = true; };
  }, [live, chatId, userId, key]);

  // Keep the newest answer in view by scrolling only the sheet or page this sits in. scrollIntoView
  // also scrolled the phone's whole screen when the sheet opened from Info, which left it blank
  // until the app was closed (Gary, 15 Oct).
  useEffect(() => {
    if (!thread.entries.length) return;
    const box = end.current?.closest('.chats-sheet, .chats-screen');
    if (box instanceof HTMLElement) box.scrollTop = box.scrollHeight;
  }, [thread.entries.length, thread.entries[thread.entries.length - 1]?.answer]);

  function patchLast(change: (e: Entry) => Entry) {
    setThread((t) => {
      const entries = t.entries.slice();
      const last = entries[entries.length - 1];
      if (last) entries[entries.length - 1] = change(last);
      return { ...t, entries };
    });
  }

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setThread((t) => ({ ...t, entries: [...t.entries, { question: q, answer: '', step: 'Thinking', error: null, done: false }] }));
    await askDrc({ text: q, drcConversationId: thread.drcConversationId, chatId }, (event) => {
      if (event.type === 'conversation') setThread((t) => ({ ...t, drcConversationId: event.id }));
      else if (event.type === 'step') patchLast((e) => ({ ...e, step: event.label }));
      else if (event.type === 'text') patchLast((e) => ({ ...e, step: null, answer: e.answer + event.text }));
      else if (event.type === 'error') patchLast((e) => ({ ...e, step: null, error: event.message, done: true }));
      else if (event.type === 'done') patchLast((e) => ({ ...e, step: null, done: true }));
    });
    patchLast((e) => ({ ...e, step: null, done: true }));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void ask(typed);
    setTyped('');
  }

  const remaining = chips.filter((c) => !thread.entries.some((e) => e.question === c));

  const askBox = (
    <>
      {remaining.length > 0 && (
        <div className="chats-drc-chips">
          {remaining.map((c) => <button key={c} type="button" disabled={busy} onClick={() => void ask(c)}>{c}</button>)}
        </div>
      )}
      <form className="chats-drc-ask" onSubmit={submit}>
        <input className="chats-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={placeholder} aria-label={`Ask ${STAFF_ASSISTANT_NAME}`} autoComplete="off" />
        <button type="submit" className="chats-send chats-send--sm" aria-label="Ask" disabled={busy || !typed.trim()}><SendIcon /></button>
      </form>
    </>
  );

  const threadView = (
      <div className="chats-drc-thread">
        {thread.entries.map((entry, i) => (
          <div key={i} className="chats-drc-pair">
            <div className="chats-drc-q">{entry.question.replace(ADDED_NOTE, '')}</div>
            <div className="chats-drc-a chats-drc-a--live">
              {entry.answer ? <DrcAnswer text={entry.answer} /> : null}
              {entry.step && <span className="chats-drc-step">{entry.step}...</span>}
              {entry.error && <span className="chats-drc-error">{entry.error}</span>}
              {entry.done && entry.answer && onUseReply && (
                <button type="button" className="chats-drc-use" onClick={() => onUseReply(plainAnswer(entry.answer))}>Use in reply</button>
              )}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
  );

  if (pageIntro) {
    return (
      <>
        <main className="chats-screen chats-drc-page">
          {pageIntro}
          {threadView}
        </main>
        <div className="chats-drc-bar">{askBox}</div>
      </>
    );
  }
  return (
    <>
      {threadView}
      {askBox}
    </>
  );
}
